import { useEffect, useState } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import { formatDate } from '@progress/kendo-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';

import PopupMenu from '../popupMenu';
import { TabContentCG } from '../tabs';
import CoverImage from '../cover-image';
import { StandardInput } from '../forms';
import HelpSupport from '../help-support';
import Dialog, { DialogProps } from './dialog';
import { HtmlEditor } from '../editor/html-editor';
import { getFileExtension } from '../../utils/files';
import MediaPreviewDialog from './media-preview-dialog';
import { uuid, uuid12 } from '../../types/common-helper';
import { GameSettingOverride } from '../../types/event';
import { PostResourceAsync } from '../../services/files';
import { InGameSetting, PreGameSetting } from '../settings';
import { Theme } from '../../pages/game-designer/resources';
import {
  ResourceWindow,
  UploadedImage
} from '../../features/game-document/image-resource/resource-window';
import { RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import { MenuList } from '../../features/game-document/maps/map-images-window';
import { useGameSettingOverride } from '../../hooks/services/use-game-setting-override';

interface GameSettingsDialogProps extends DialogProps {
  gameId: number;
  playlistId: number;
  eventName: string;
  settingOverride?: GameSettingOverride;
  onCancel: () => void;
  onConfirm: (settingOverride: GameSettingOverride) => void;
}

export const extractBlobResourcesAsync = async (
  gameId: string,
  settingOverride: GameSettingOverride
) => {
  const dateString = formatDate(new Date(), 'yyMMdd');
  const fileNamePrefix = `${gameId}-${dateString}`;
  const resourceEntity = settingOverride.resources;

  const blobToUrl = async (value: string, description: string) => {
    const blob = await fetch(value).then((r) => r.blob());
    const fileName = `${fileNamePrefix}-${uuid12()}.${getFileExtension(
      description
    )}`;
    const extractedBlob: File = new File([blob], fileName, {
      type: blob.type
    });

    const newResourceResponse = await PostResourceAsync(extractedBlob);
    return newResourceResponse;
  };

  for (const resource of resourceEntity) {
    if (!resource) continue;

    if (
      resource.value && typeof resource.value === 'string'
        ? resource.value.startsWith('blob:')
        : false
    ) {
      try {
        if (resource.value !== undefined) {
          const newResourceResponse = await blobToUrl(
            resource.value,
            resource.description
          );
          resource.value = newResourceResponse.value ?? '';
        }
      } catch (ex) {
        console.log(ex);
      }
    }
  }

  if (settingOverride.theme) {
    const { logoUrl, footerUrl } = settingOverride.theme;
    if (logoUrl) {
      const url = await blobToUrl(logoUrl, '');
      settingOverride.theme.logoUrl = url.value ?? '';
    }

    if (footerUrl) {
      const url = await blobToUrl(footerUrl, '');
      settingOverride.theme.footerUrl = url.value ?? '';
    }
  }

  return settingOverride;
};

const GameSettingsDialog = (props: GameSettingsDialogProps) => {
  const { id: eventId } = useParams();
  const {
    settingOverride,
    eventName,
    gameId,
    playlistId,
    onConfirm,
    onCancel
  } = props;
  const [state, setState] = useState<{
    isDirty: boolean;
    isValid: boolean;
    content?: GameSettingOverride;
  }>({
    isDirty: false,
    isValid: true
  });
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [fileUpload, setFileUpload] = useState<'Logo' | 'Media'>('Logo');
  const [openFileDialog, setOpenFileDialog] = useState<boolean>(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [editMediaResource, setEditMediaResource] = useState<{
    index: number;
    resId: string;
  }>({
    index: 0,
    resId: ''
  });
  const [previewItem, setPreviewItem] = useState<string>('');
  const [previewItemType, setPreviewItemType] = useState<string>('');

  const { content, isLoading } = useGameSettingOverride(
    settingOverride,
    eventId,
    playlistId,
    gameId
  );

  const toggleEntityEditor = () => setOpenFileDialog(!openFileDialog);

  const getResourceValue = (id: string) => {
    if (!state.content) return '';
    const resource = state.content.resources.find((r) => r?.id === id);
    if (!resource) return '';
    return resource.value ?? '';
  };

  const onEditLogo = () => {
    setFileUpload('Logo');
    setSelectedImageUrl(
      getResourceValue(state.content?.overview.logoResId ?? '')
    );
    setOpenFileDialog(true);
  };

  const onAddMedia = () => {
    setFileUpload('Media');
    setOpenFileDialog(true);
  };

  const handleChangeState = <T extends keyof GameSettingOverride>(
    prop: T,
    value: GameSettingOverride[T]
  ) => {
    setState((prev) => ({
      ...prev,
      isDirty: true,
      content: {
        ...prev.content!,
        [prop]: value
      }
    }));
  };

  const createNewResource = (
    name: string,
    value: string,
    type: string,
    size?: number,
    category?: string
  ) => {
    if (!state.content) return;

    const resources = state.content.resources;
    const newResource = {
      id: uuid(),
      name,
      type,
      value,
      description: name,
      category,
      ...(size !== undefined && { size })
    };

    handleChangeState('resources', [...resources, newResource]);
    return newResource;
  };

  const updateResourceById = (
    id: string,
    value: string,
    fileName?: string,
    category?: string
  ) => {
    if (!state.content) return;

    const resources = cloneDeep(state.content.resources);
    const selectedResourceId = resources.findIndex((r) => r?.id === id);
    if (selectedResourceId !== -1) {
      const selectedResource = resources[selectedResourceId];
      if (selectedResource) {
        selectedResource.value = value;
        if (fileName) {
          selectedResource.name = fileName;
          selectedResource.description = fileName;
        }
        if (category) {
          selectedResource.category = category;
        }
      }

      resources.splice(selectedResourceId, 1, selectedResource);
      handleChangeState('resources', resources);
    }
  };

  const deleteResourceById = (id: string) => {
    if (!state.content) return;

    const resources = cloneDeep(state.content.resources);
    const selectedResourceId = resources.findIndex((r) => r?.id === id);
    if (selectedResourceId !== -1) {
      resources.splice(selectedResourceId, 1);
      handleChangeState('resources', resources);
    }
  };

  const onChangeOverview = <T extends keyof GameSettingOverride['overview']>(
    prop: T,
    value: GameSettingOverride['overview'][T]
  ) => {
    if (!state.content) return;

    const overview = state.content.overview;
    handleChangeState('overview', {
      ...overview,
      [prop]: value
    });
  };

  const onMenuSelected = (id: number, menu: MenuList, idString?: string) => {
    if (!state.content || !idString) return;

    setFileUpload('Media');
    const medias = cloneDeep(state.content.overview.medias);
    const selectedMedia = medias.at(parseInt(idString));

    if (!selectedMedia) return;

    if (menu.textMenu === 'Delete') {
      medias.splice(parseInt(idString), 1);
      onChangeOverview('medias', medias);
      deleteResourceById(selectedMedia.value);
    }

    if (menu.textMenu === 'Edit') {
      setSelectedImageUrl(getResourceValue(selectedMedia.value));
      setEditMediaResource({
        index: parseInt(idString),
        resId: selectedMedia.value
      });

      setOpenFileDialog(true);
    }
  };

  const handleEntityEditorSubmit = (
    uploadedImage: UploadedImage,
    target: 'Logo' | 'Media'
  ) => {
    if (!state.content) return;
    const { fileName, blobUrl, mimeType, size, category } = uploadedImage;
    const resourceType = mimeType?.split('/')[0];

    if (target === 'Logo') {
      const logoResId = state.content?.overview.logoResId;

      if (!logoResId) {
        const newRes = createNewResource(fileName, blobUrl, resourceType, size, category);
        if (newRes) onChangeOverview('logoResId', newRes.id);
      }

      updateResourceById(logoResId, blobUrl, fileName, category);
    }

    if (target === 'Media') {
      const medias = cloneDeep(state.content.overview.medias);
      if (!editMediaResource.index && !editMediaResource.resId) {
        const newResource = createNewResource(
          fileName,
          blobUrl,
          resourceType,
          size,
          category
        );
        onChangeOverview('medias', [
          ...medias,
          { type: resourceType, value: newResource?.id ?? '', category }
        ]);
      } else {
        const selectedMedia = medias[editMediaResource.index];
        selectedMedia.type = resourceType;
        selectedMedia.category = category;
        medias.splice(editMediaResource.index, 1, selectedMedia);
        onChangeOverview('medias', medias);
        updateResourceById(selectedMedia.value, blobUrl, fileName, category);

        setEditMediaResource({
          index: 0,
          resId: ''
        });
      }
    }

    if (openFileDialog) {
      setOpenFileDialog(false);
    }
  };

  const onChangePreAndInGame = (
    type: 'preGame' | 'inGame',
    { name, value }: { name: string; value: boolean }
  ) => {
    if (!state.content) return;

    const settings = state.content.settings;
    handleChangeState('settings', {
      ...settings,
      [type]: {
        ...settings[type],
        [name]: value
      }
    });
  };

  const handleConfirm = async () => {
    if (!state.content) return;
    onConfirm(state.content);
  };

  useEffect(() => {
    if (!content) return;
    setState((prev) => {
      if (prev.isDirty) return prev;
      if (isEqual(prev.content, content)) return prev;
      return { ...prev, content };
    });
  }, [content]);

  return (
    <>
      <Dialog
        className={'override-dialog'}
        onClose={onCancel}
        width={'60%'}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <span style={{ maxWidth: '50%', textWrap: 'wrap' }}>
              {eventName +
                `${state.content ? ` - ${getResourceValue(state.content.overview.titleResId)}` : ''}`}
            </span>
            <span
              style={{
                fontSize: '12px'
              }}>
              <HelpSupport
                title="My Event - Game Settings + Theme"
                url="https://forum.catalystglobal.com/t/4944"
              />
            </span>
          </div>
        }
        {...props}>
        {isLoading || !state.content ? (
          <div
            className={
              'd-flex flex-column align-items-center justify-content-center w-100 gap-2 my-4'
            }>
            <span className={'loader'}></span>
            <label className={'text-gray'}>Loading...</label>
          </div>
        ) : (
          <>
            <Container className="setting-container pl-0" fluid>
              <TabContentCG
                selected={selectedTab}
                handleSelect={(e) => setSelectedTab(e.selected)}
                items={[
                  {
                    title: 'Overview',
                    content: (
                      <>
                        <Row className={'mt-2'}>
                          <Col xs={'auto'}>
                            <Label>Icon</Label>
                            <CoverImage
                              imageUrl={getResourceValue(
                                state.content.overview.logoResId
                              )}
                              containerStyle={'rounded'}
                              isfullWidth={false}>
                              <Button
                                fillMode={'flat'}
                                onClick={onEditLogo}
                                rounded={'full'}>
                                <span className={'material-symbols-outlined'}>
                                  more_vert
                                </span>
                              </Button>
                            </CoverImage>
                          </Col>
                          <Col>
                            <StandardInput
                              label={'Title'}
                              name={'Title'}
                              value={getResourceValue(
                                state.content.overview.titleResId
                              )}
                              onChange={(e) => {
                                updateResourceById(
                                  state.content?.overview.titleResId ?? '',
                                  e.value
                                );

                                setState((prev) => ({
                                  ...prev,
                                  isValid: e.value.length > 0
                                }));
                              }}
                              required
                              validationMessage={
                                !getResourceValue(
                                  state.content.overview.titleResId
                                )
                                  ? 'This field is required'
                                  : ''
                              }
                              maxLength={256}
                            />
                            <Col xs={12} className="mt-2">
                              <Col sm={12} md={12}>
                                <Label>Information for Players</Label>
                                <HtmlEditor
                                  displayCounter
                                  fieldName="Information for Players"
                                  className={'information-players-editor'}
                                  limit={15000}
                                  value={getResourceValue(
                                    state.content.overview.longDescriptionResId
                                  )}
                                  handleHtmlChange={(e) =>
                                    updateResourceById(
                                      state.content?.overview
                                        .longDescriptionResId ?? '',
                                      e.html
                                    )
                                  }
                                />
                              </Col>
                            </Col>
                          </Col>
                        </Row>

                        <Row className={'mt-2 gy-2'}>
                          <Col xs={12}>
                            <Label>Game Media</Label>
                          </Col>
                          <Col xs={12}>
                            <div className={'thumb-nails d-flex flex-wrap'}>
                              {state.content.overview.medias.length > 0 &&
                                state.content.overview.medias.map(
                                  (file, index) => (
                                    <div className={'d-flex'} key={index}>
                                      <div
                                        key={index}
                                        className={
                                          'w-8 h-8 bg-light position-relative rounded d-flex overflow-hidden flex-column justify-content-center align-items-center cursor-pointer'
                                        }
                                        onClick={() => {
                                          const selectedResource =
                                            state.content?.resources.find(
                                              (r) => r?.id === file.value
                                            );
                                          if (
                                            selectedResource &&
                                            selectedResource.value &&
                                            selectedResource.type
                                          ) {
                                            setPreviewItemType(
                                              selectedResource.type
                                            );
                                            setPreviewItem(
                                              selectedResource.value
                                            );
                                          }
                                        }}>
                                        {file && file.type === 'video' && (
                                          <div
                                            className={
                                              'mask position-absolute top-0 end-0 h-full w-full d-flex align-items-center justify-content-center'
                                            }
                                            style={{
                                              backgroundImage: `url("${
                                                getResourceValue(file.value) ??
                                                ''
                                              }")`,
                                              backgroundPosition: 'center'
                                            }}>
                                            <button
                                              className={'btn text-light'}>
                                              <span
                                                className={
                                                  'material-symbols-outlined'
                                                }>
                                                play_arrow
                                              </span>
                                            </button>
                                          </div>
                                        )}
                                        {file && file.type === 'image' && (
                                          <img
                                            src={getResourceValue(file.value)}
                                            className={'rounded'}
                                            alt={''}
                                          />
                                        )}
                                      </div>
                                      <PopupMenu
                                        menus={[
                                          {
                                            classIcon: 'edit',
                                            textMenu: 'Edit',
                                            textClass: ''
                                          },
                                          {
                                            classIcon: 'delete',
                                            textMenu: 'Delete',
                                            textClass: 'text-danger'
                                          }
                                        ]}
                                        id={
                                          Math.floor(
                                            Math.random() * (30 - 1 + 1)
                                          ) + 1
                                        }
                                        onMenuSelected={onMenuSelected}
                                        idString={index.toString()}
                                      />
                                    </div>
                                  )
                                )}
                              <div
                                className={
                                  'w-8 h-8 border border-1 bg-light rounded d-flex flex-column justify-content-center align-items-center cursor-pointer text-decoration-none'
                                }
                                onClick={onAddMedia}>
                                <span
                                  className={
                                    'material-symbols-outlined text-primary'
                                  }>
                                  add_photo_alternate
                                </span>
                                Add
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </>
                    )
                  },
                  {
                    title: 'Pre-Game',
                    content: (
                      <PreGameSetting
                        state={state.content.settings.preGame}
                        onChange={(name, value) =>
                          onChangePreAndInGame('preGame', { name, value })
                        }
                      />
                    )
                  },
                  {
                    title: 'In-Game',
                    content: (
                      <InGameSetting
                        state={state.content.settings.inGame}
                        onChange={(name, value) =>
                          onChangePreAndInGame('inGame', { name, value })
                        }
                      />
                    )
                  },
                  {
                    title: 'Theme',
                    content: (
                      <Theme
                        hideResourcesTab
                        theme={state.content.theme}
                        setTheme={(newTheme) => {
                          handleChangeState('theme', newTheme);
                        }}
                      />
                    )
                  }
                ]}
              />
            </Container>
            <div
              className={
                'position-sticky bottom-0 bg-white d-flex w-100 justify-content-end align-items-center gap-3 py-3'
              }>
              <Button
                themeColor={'secondary'}
                className="w-10"
                onClick={onCancel}>
                Cancel
              </Button>
              <Button
                disabled={!state.isDirty || !state.isValid}
                themeColor={'primary'}
                className="w-10"
                onClick={handleConfirm}>
                Confirm
              </Button>
            </div>
          </>
        )}
      </Dialog>

      {openFileDialog && (
        <ResourceWindow
          toggleDialog={toggleEntityEditor}
          onSubmit={(e) => handleEntityEditorSubmit(e, fileUpload)}
          fileType={fileUpload === 'Logo' ? ['image'] : ['image', 'video']}
          imageUrl={selectedImageUrl}
          hideResourcesTab={true}
        />
      )}

      {previewItem && previewItemType && (
        <MediaPreviewDialog
          title="Media Preview"
          mediaType={previewItemType}
          mediaUrl={previewItem}
          onClose={() => {
            setPreviewItem('');
            setPreviewItemType('');
          }}
        />
      )}
    </>
  );
};

export default GameSettingsDialog;
